import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import BlogCards from './BlogCards';
import UpcomingEvents from '../components/UpcomingEvents';
import AboutUsSection from '../components/AboutUsSection';
import HeroBackground from '../components/HeroBackground';

import TeamSection from '../components/TeamSection';
import ContactSection from '../components/ContactSection';
import GroupsSection from '../components/GroupsSection';

import axios from 'axios';
import { trackCTAClick } from '../utils/analytics';
import { ArrowRightIcon, ChevronRightIcon, ChevronDownIcon } from '../components/Icons';
import { useTheme, useMediaQuery, Button, ButtonGroup } from '@mui/material';
const Home = () => {
  const [heroStage, setHeroStage] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [activeWizard, setActiveWizard] = useState('contact');

  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollPosition(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const delays = [150, 320, 520, 760];
    const timers = delays.map((delay, index) =>
      window.setTimeout(() => {
        setHeroStage(index + 1);
      }, delay)
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL || 'https://pfadfinder-public-api.suppenchris.workers.dev/api'}/settings/homepage`);
        setHomepageSettings(data);
      } catch (err) {
        console.error('Failed to fetch homepage settings', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const stammName = homepageSettings?.organization?.stammName || 'Pfadfinder';
    const motto = homepageSettings?.organization?.motto || 'Natur erleben, Freunde finden, fürs Leben lernen.';

    setMeta('bob-type', 'home');
    setMeta('bob-title', `${stammName} Homepage`);
    setMeta('bob-preview', `Die offizielle Webseite des Pfadfinderstammes ${stammName}. ${motto}`);
    setMeta('bob-bubble', 'Hallo! Ich bin Bob – frag mich einfach etwas! ⚜️');

    return () => {
      document.querySelector('meta[name="bob-type"]')?.remove();
      document.querySelector('meta[name="bob-title"]')?.remove();
      document.querySelector('meta[name="bob-preview"]')?.remove();
      document.querySelector('meta[name="bob-bubble"]')?.remove();
    };
  }, [homepageSettings]);

  const bildDynamicStyle = {
    filter: `brightness(${1 + Math.min(1, scrollPosition / 400) * 0.15})`,
    transition: 'filter 0.1s ease-out',
  };

  const [isIntroOpen, setIsIntroOpen] = useState(true);

  const handleSchnupper = () => {
    setActiveWizard('schnupper');
    setTimeout(() => {
      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleCtaClick = (type, url, label, section) => {
    trackCTAClick(label, section);
    if (type === 'schnupper') {
      handleSchnupper();
    } else if (type === 'scroll' || url?.startsWith('#')) {
      const targetId = url.replace('#', '');
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Helper values with fallbacks
  const hero = homepageSettings?.hero || {};
  const heroTitle = hero.title || 'Entdecke die Welt mit den Pfadfindern in Wutöschingen!';
  const heroSubtitle = hero.subtitle || 'Natur erleben, Freunde finden, fürs Leben lernen.';
  const primaryCtaText = hero.primaryCtaText || 'Hier anmelden';
  const primaryCtaUrl = hero.primaryCtaUrl || 'https://on.campflow.de/pfadfinderbund-horizonte/mitgliedsantrag';
  const primaryCtaType = hero.primaryCtaType || 'link';
  const secondaryCtaText = hero.secondaryCtaText || 'Direkt reinschnuppern';
  const secondaryCtaUrl = hero.secondaryCtaUrl || '#contact-section';
  const secondaryCtaType = hero.secondaryCtaType || 'schnupper';

  const intro = homepageSettings?.intro || {};
  const introTitle = intro.title || 'Willkommen bei den Pfadfindern Wutöschingen!';
  const introSubtitle = intro.subtitle || 'Schön, dass du da bist! Erfahre hier mehr über unseren Stamm und wie du Teil unserer Gemeinschaft werden kannst.';
  const mainParagraph1 = intro.mainParagraph1 !== undefined
    ? intro.mainParagraph1
    : 'Wir sind ein brandneues Pfadfinderprojekt, das gerade in Wutöschingen entsteht! Als motivierte und engagierte Gruppe können wir es kaum erwarten, gemeinsam mit euch die Natur zu entdecken, neue Freundschaften zu schließen und unvergessliche Abenteuer zu erleben.';
  const mainParagraph2 = intro.mainParagraph2 !== undefined
    ? intro.mainParagraph2
    : 'Ob Lagerfeuer unter dem Sternenhimmel, spannende Ausflüge in die Natur oder kreative Projekte – bei uns ist für jeden etwas dabei. Sei dabei und gestalte mit uns die Zukunft der Pfadfinder in unserer Gemeinde!';
  const bundCard = intro.bundCard || {
    show: true,
    logoUrl: 'https://wsrv.nl/?url=https%3A%2F%2Fpbhorizonte.de%2Fwp-content%2Fuploads%2F2021%2F02%2Fcropped-PBH-freigestellt.png&w=300&output=webp',
    title: 'Pfadfinderbund Horizonte e.V.',
    description1: 'Der Pfadfinderbund Horizonte e.V. ist ein ehrenamtlicher Verein, der seit über 50 Jahren erfolgreiche Jugendarbeit in Baden-Württemberg leistet. Mit Gruppen in bereits vier Orten sind wir Teil einer großen Pfadfinder-Familie. Die Neugründung in Wutöschingen markiert einen aufregenden neuen Meilenstein – und du kannst von Anfang an dabei sein!',
    description2: 'Unsere Ziele: Abenteuer erleben, Verantwortung übernehmen, Gemeinschaft stärken und die Natur schützen. Gemeinsam schaffen wir einen Ort, an dem junge Menschen wachsen und fürs Leben lernen können.',
    buttonText: 'Mehr über den Bund erfahren',
    buttonUrl: 'https://pbhorizonte.de/'
  };

  const defaultSectionsOrder = [
    { id: 'intro', enabled: true, name: 'Willkommen & Bund' },
    { id: 'about', enabled: true, name: 'Werte & Über uns' },
    { id: 'groups', enabled: true, name: 'Gruppen & Stufen' },
    { id: 'blog', enabled: true, name: 'Aktuelles / Blog' },
    { id: 'team', enabled: true, name: 'Unser Team' },
    { id: 'events', enabled: true, name: 'Kommende Termine' },
    { id: 'contact', enabled: true, name: 'Kontakt & Schnuppern' }
  ];

  const sectionsOrder = Array.isArray(homepageSettings?.sectionsOrder) && homepageSettings.sectionsOrder.length > 0
    ? homepageSettings.sectionsOrder
    : defaultSectionsOrder;

  // Render individual sections dynamically
  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'intro':
        return (
          <section key="intro" id="intro-section" className="bg-surface-containerLow section-reveal delay-150 scroll-mt-28">
            <div className="pb-16 md:pb-24 container mx-auto px-6 max-w-5xl">
              <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-end mb-12">
                  <div className="space-y-2">
                    <h2 className="text-headline-large md:text-display-small font-bold text-surface-on">
                      {introTitle}
                    </h2>
                    <p className="text-body-large text-surface-onVariant max-w-2xl">
                      {introSubtitle}
                    </p>
                  </div>

                  <button
                    onClick={() => setIsIntroOpen(!isIntroOpen)}
                    className="md3-btn-icon-unfilled w-12 h-12 flex items-center justify-center rounded-full bg-surface-containerHigh text-surface-on hover:bg-primary/10 hover:text-primary transition-all duration-300 shadow-elevation-1 cursor-pointer"
                    aria-label={isIntroOpen ? "Sektion einklappen" : "Sektion ausklappen"}
                  >
                    <ChevronRightIcon
                      className={`transition-transform duration-300 ${isIntroOpen ? 'rotate-90' : ''}`}
                      sx={{ fontSize: 32 }}
                    />
                  </button>
                </div>

                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isIntroOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="pt-4">
                    {/* Haupttext - MD3 Filled Card */}
                    {(mainParagraph1 || mainParagraph2) && (
                      <div className="md3-card-filled p-6 md:p-8 mb-8">
                        {mainParagraph1 && (
                          <p className="text-body-large mb-4 text-surface-onVariant">
                            {mainParagraph1}
                          </p>
                        )}
                        {mainParagraph2 && (
                          <p className="text-body-large text-surface-onVariant">
                            {mainParagraph2}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Horizonte Bund Card - MD3 Outlined Card */}
                    {bundCard?.show !== false && homepageSettings?.features?.bundCard !== false && (
                      <div className="md3-card-outlined p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
                          <div className="flex justify-center">
                            <div className="p-4 bg-surface-containerLowest rounded-xl w-full max-w-[180px] aspect-square flex items-center justify-center">
                              <img
                                src={bundCard.logoUrl || "https://wsrv.nl/?url=https%3A%2F%2Fpbhorizonte.de%2Fwp-content%2Fuploads%2F2021%2F02%2Fcropped-PBH-freigestellt.png&w=300&output=webp"}
                                alt={bundCard.title || "Bund Logo"}
                                width="180"
                                height="180"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <h3 className="text-title-large font-bold mb-3 text-surface-on">
                              {bundCard.title || 'Pfadfinderbund Horizonte e.V.'}
                            </h3>
                            {bundCard.description1 && (
                              <p className="text-body-medium mb-3 text-surface-onVariant">
                                {bundCard.description1}
                              </p>
                            )}
                            {bundCard.description2 && (
                              <p className="text-body-medium mb-5 text-surface-onVariant">
                                {bundCard.description2}
                              </p>
                            )}

                            {bundCard.buttonUrl && (
                              <a
                                href={bundCard.buttonUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="md3-btn-outlined inline-flex items-center gap-2"
                                onClick={() => trackCTAClick('bund_webseite_intro', 'intro_section')}
                              >
                                {bundCard.buttonText || 'Mehr über den Bund erfahren'} <ArrowRightIcon sx={{ fontSize: 18 }} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );

      case 'about':
        return (
          <section key="about" id="about-section" className="section-reveal delay-200 scroll-mt-28">
            <AboutUsSection data={homepageSettings?.about} />
          </section>
        );

      case 'groups':
        return (
          <section key="groups" id="groups-section" className="section-reveal delay-220 scroll-mt-28">
            <GroupsSection header={homepageSettings?.sectionHeaders?.groups} />
          </section>
        );

      case 'blog':
        return (
          <section key="blog" id="blog-section" className="section-reveal delay-250 scroll-mt-28">
            <BlogCards header={homepageSettings?.sectionHeaders?.blog} />
          </section>
        );

      case 'team':
        return (
          <div key="team" className="section-reveal delay-300">
            <TeamSection header={homepageSettings?.sectionHeaders?.team} />
          </div>
        );

      case 'events':
        return (
          <section key="events" id="events-section" className="section-reveal delay-350 scroll-mt-28">
            <UpcomingEvents header={homepageSettings?.sectionHeaders?.events} />
          </section>
        );

      case 'contact':
        return (
          <div key="contact" className="section-reveal delay-400">
            <ContactSection
              header={homepageSettings?.sectionHeaders?.contact}
              organization={homepageSettings?.organization}
              activeWizard={activeWizard}
              onWizardChange={(wizard) => setActiveWizard(wizard)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-surface-containerLow min-h-screen flex flex-col font-sans text-surface-on">
      {/* Hero Section */}
      <section id="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        <HeroBackground
          isMobile={isMobile}
          scrollPosition={scrollPosition}
          bildDynamicStyle={bildDynamicStyle}
          settings={homepageSettings}
        />

        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
          <div className="space-y-8">
            <h1 className="text-display-small md:text-display-medium font-bold text-white drop-shadow-xl">
              {heroTitle}
            </h1>
            <p
              className={`text-body-large md:text-headline-small text-white/90 drop-shadow-md transition-all duration-700 ease-out delay-150 ${heroStage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              {heroSubtitle}
            </p>
            {homepageSettings?.features?.heroCta !== false && (
              <div
                className={`transition-all duration-700 ease-out delay-200 ${heroStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <ButtonGroup variant="contained" aria-label="CTA buttons" sx={{ borderRadius: '50px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  <Button
                    onClick={() => handleCtaClick(secondaryCtaType, secondaryCtaUrl, 'schnupper_hero', 'hero_section')}
                    sx={{
                      py: 1.5,
                      px: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderRadius: 0,
                      backgroundColor: 'rgb(var(--color-secondary-container))',
                      color: 'rgb(var(--color-secondary-on-container))',
                      '&:hover': {
                        backgroundColor: 'rgb(var(--color-secondary-container))',
                        opacity: 0.8,
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {secondaryCtaText}
                    <ChevronDownIcon sx={{ fontSize: '1.2rem' }} />
                  </Button>
                  <Button
                    onClick={() => handleCtaClick(primaryCtaType, primaryCtaUrl, 'mitgliedsantrag_hero', 'hero_section')}
                    sx={{
                      py: 1.5,
                      px: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderRadius: 0,
                      backgroundColor: 'rgb(var(--color-primary))',
                      color: 'rgb(var(--color-primary-on))',
                      '&:hover': {
                        backgroundColor: 'rgb(var(--color-primary))',
                        opacity: 0.8,
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {primaryCtaText}
                    <ArrowRightIcon sx={{ fontSize: '1.2rem' }} />
                  </Button>
                </ButtonGroup>
              </div>
            )}
            <div
              className={`mx-auto mt-12 max-w-max text-white/90 transition-all duration-700 ease-out ${heroStage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <button 
                onClick={() => {
                  const firstSection = sectionsOrder.find(s => s.enabled !== false)?.id || 'intro';
                  document.getElementById(`${firstSection}-section`)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center text-white/90 hover:text-white cursor-pointer transition-colors"
                aria-label="Nach unten scrollen"
              >
                <ChevronDownIcon
                  className="animate-bounce-down"
                  sx={{ fontSize: '2.2rem' }}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Navbar />

      {/* Dynamic Sections in User-Configured Order */}
      {sectionsOrder
        .filter(section => {
          if (section.enabled === false) return false;
          const modules = homepageSettings?.modules || {};
          if (section.id === 'groups' && modules.groups === false) return false;
          if (section.id === 'team' && modules.team === false) return false;
          if (section.id === 'blog' && modules.blog === false) return false;
          if (section.id === 'events' && modules.events === false) return false;
          return true;
        })
        .map(section => renderSection(section.id))}
    </div>
  );
};

export default Home;
